import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Alert from 'components/Alert/Alert';

import { selectActiveApplication } from 'features/applications/applicationsSelectors';

import { useInterviewState } from 'hooks/useInterviewState';
import usePageviewEvent from 'hooks/usePageViewEvent';
import { useSyncApplicationRoute } from 'hooks/useSyncApplicationRoute';

import {
  AlertType,
  ApplicationStage,
  GTMEvents,
  InterviewStep
} from 'constants/enums';
import { TOUR_STEP_TARGET_APPLICATION_INTERVIEW_TAB } from 'constants/tour';

import styles from './InterviewView.module.scss';
import AwaitingResponse from './components/Views/AwaitingResponse/AwaitingResponse';
import ConfirmInterview from './components/Views/ConfirmInterview/ConfirmInterview';
import InterviewCanceled from './components/Views/InterviewCanceled/InterviewCanceled';
import InterviewDeclined from './components/Views/InterviewDeclined/InterviewDeclined';
import InterviewScheduled from './components/Views/InterviewScheduled/InterviewScheduled';
import RequestInterview from './components/Views/RequestInterview/RequestInterview';
import LoadingState from './components/Views/components/LoadingState/LoadingState';

interface InterviewViewProps {
  fetchApplicationData: (applicationId: string) => Promise<void>;
}

const InterviewView: FC<InterviewViewProps> = ({
  fetchApplicationData
}: InterviewViewProps) => {
  const { t } = useTranslation();
  const application = useSelector(selectActiveApplication);

  const {
    applicationId,
    isLoading,
    setIsLoading,
    interviewRequest,
    isInterviewRequestSent,
    fetchInterviewRequest
  } = useInterviewState();

  useSyncApplicationRoute(applicationId);
  usePageviewEvent(GTMEvents.ApplicationsListViewSidebarInterview);

  const handleRequestSent = async () => {
    await fetchInterviewRequest();
    await fetchApplicationData(applicationId);
  };

  const handleConfirmed = async () => {
    await fetchInterviewRequest();
    await fetchApplicationData(applicationId);
  };

  const tourStepId = TOUR_STEP_TARGET_APPLICATION_INTERVIEW_TAB;

  const isHired = application?.stage === ApplicationStage.HiredApplicationStage;

  const isDeclined =
    application?.stage === ApplicationStage.DeclinedApplicationStage;

  const renderContent = () => {
    // TODO: BE currently has no logic to change interview status to Declined when Application stage
    // changes to Declined. If backend provides this functionality, we need to remove this logic as
    // interviewRequest.status === DECLINED already renders InterviewDeclined component
    if (isDeclined) {
      return (
        <div id={tourStepId}>
          <InterviewDeclined />
        </div>
      );
    }

    if (isHired) {
      return (
        <div>
          <div className={styles.title}>
            {t('sidebar.application.interview.unavailable')}
          </div>
          <Alert type={AlertType.Info}>
            {t('sidebar.application.interview.hired')}
          </Alert>
        </div>
      );
    }

    // If interview request is not sent, show request interview form
    if (!isInterviewRequestSent) {
      return (
        <div id={tourStepId}>
          <RequestInterview
            isLoading={isLoading}
            onLoading={setIsLoading}
            onRequestSent={handleRequestSent}
          />
        </div>
      );
    }

    // If interview request is sent but data is still loading
    if (!interviewRequest) {
      return (
        <div id={tourStepId}>
          <LoadingState
            title={t('sidebar.application.interview.interviewProcess')}
          />
        </div>
      );
    }

    // Render based on interview status
    switch (interviewRequest.status) {
      case InterviewStep.InterviewRequestSent:
        return (
          <div id={tourStepId}>
            <AwaitingResponse
              isLoading={isLoading}
              onLoading={setIsLoading}
              interviewRequest={interviewRequest}
              onActionCompleted={fetchInterviewRequest}
            />
          </div>
        );

      case InterviewStep.SlotProvided:
        return (
          <div id={tourStepId}>
            <ConfirmInterview
              interviewRequest={interviewRequest}
              onConfirmed={handleConfirmed}
              isLoading={isLoading}
              onLoading={setIsLoading}
            />
          </div>
        );

      case InterviewStep.Scheduled:
        return (
          <div id={tourStepId}>
            <InterviewScheduled interviewDetails={interviewRequest} />
          </div>
        );

      case InterviewStep.Withdrawn:
        return (
          <div id={tourStepId}>
            <InterviewCanceled />
          </div>
        );

      case InterviewStep.Declined:
        return (
          <div id={tourStepId}>
            <InterviewDeclined />
          </div>
        );

      default:
        return (
          <div id={tourStepId}>
            <LoadingState
              title={t('sidebar.application.interview.interviewProcess')}
            />
          </div>
        );
    }
  };

  return <>{renderContent()}</>;
};

export default InterviewView;
